import { Modal, type SxProps, type Theme } from "@mui/material";
import { useReducedMotion } from "motion/react";
import { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { useRtl } from "@ryde/redux";
import { FocusableElementTags, KeyboardKeys } from "@ryde/shared";

import { MuiButton } from "../forms";
import {
  MODAL_DIALOG_ENTRY_TRANSITION,
  MODAL_DIALOG_ENTRY_VARIANTS,
} from "./modal.constants";
import {
  ModalContainerMotion,
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
} from "./modal.styled";

export interface IModalDialog extends PropsWithChildren {
  title: string;
  /** Merged onto the modal shell (e.g. `MODAL_DIALOG_FILL_CONTENT_HEIGHT_CLASS` for full-height body on small viewports). */
  containerClassName?: string;
  showContent?: boolean;
  showFooter?: boolean;
  showOkButton?: boolean;
  isModalOpen?: boolean;
  width?: string;
  closeModalClick: (
    event?: Event | React.KeyboardEvent<HTMLDivElement>,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => void;
  closeModalHeaderClick?: () => void;
  saveClick?: () => void;
  saveButtonTitle?: string;
  closeButtonTitle?: string;
  isSaveButtonDisabled?: boolean;
  sx?: SxProps<Theme> | undefined;
  enableEscape?: boolean; // not implemented yet
  shouldAnimateEntry?: boolean;
}

export const ModalDialog: FC<IModalDialog> = ({
  title,
  isModalOpen,
  closeModalClick,
  closeModalHeaderClick,
  saveClick,
  children,
  containerClassName,
  showFooter = true,
  showContent = true,
  width,
  showOkButton = true,
  saveButtonTitle,
  closeButtonTitle,
  isSaveButtonDisabled,
  shouldAnimateEntry,
}) => {
  const { t } = useTranslation();
  const { direction } = useRtl();
  const shouldReduceMotion = useReducedMotion();
  const shouldPlayEntryMotion =
    Boolean(shouldAnimateEntry) && !shouldReduceMotion;
  const okButtonTitle = saveButtonTitle ?? t(`general.SAVE`);
  closeButtonTitle =
    closeButtonTitle ??
    (showOkButton ? t(`general.CANCEL`) : t(`general.CLOSE`));

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const activeElement = document.activeElement as HTMLElement;
    const focusableElements = new Set<FocusableElementTags>([
      FocusableElementTags.BUTTON,
      FocusableElementTags.INPUT,
      FocusableElementTags.TEXTAREA,
      FocusableElementTags.SELECT,
    ]);
    const isFocusable =
      activeElement &&
      focusableElements.has(activeElement.tagName as FocusableElementTags);

    if (event.key === KeyboardKeys.ESCAPE) {
      closeModalClick(event, "escapeKeyDown");
    } else if (
      event.key === KeyboardKeys.ENTER &&
      !event.repeat &&
      !isFocusable
    ) {
      event.preventDefault();
      if (!isSaveButtonDisabled && saveClick) {
        saveClick();
      }
    }
  };

  const handleCloseModalHeaderClick = (): void => {
    if (closeModalHeaderClick) {
      closeModalHeaderClick();
    } else {
      closeModalClick();
    }
  };

  const panel = (
    <>
      <ModalHeaderStyled>
        <h1>{title}</h1>
        <SVG_MUI.XMarkSVG onClick={handleCloseModalHeaderClick} />
      </ModalHeaderStyled>
      <ModalContentStyled $showContent={showContent}>
        {children}
      </ModalContentStyled>
      {showFooter && (
        <ModalFooterStyled>
          <MuiButton
            muiStyle={"primary"}
            muiVariant={"outlined"}
            onClick={() => closeModalClick()}
            e2eData="cancel-modal-button"
          >
            {closeButtonTitle}
          </MuiButton>
          {showOkButton && (
            <MuiButton
              onClick={() => saveClick?.()}
              disabled={isSaveButtonDisabled}
              e2eData="ok-modal-button"
            >
              {okButtonTitle}
            </MuiButton>
          )}
        </ModalFooterStyled>
      )}
    </>
  );

  return (
    <Modal
      open={isModalOpen || false}
      closeAfterTransition={true}
      onKeyDown={handleKeyDown}
    >
      {shouldPlayEntryMotion ? (
        <ModalContainerMotion
          width={width}
          dir={direction}
          className={containerClassName}
          $skipCssCenter
          variants={MODAL_DIALOG_ENTRY_VARIANTS}
          initial="hidden"
          animate="visible"
          transition={MODAL_DIALOG_ENTRY_TRANSITION}
        >
          {panel}
        </ModalContainerMotion>
      ) : (
        <ModalContainerStyled
          width={width}
          dir={direction}
          className={containerClassName}
        >
          {panel}
        </ModalContainerStyled>
      )}
    </Modal>
  );
};
